import { defineStore } from "pinia";
import type { Locale } from "@/i18n/text";

/** 与原型顶栏一致的语言选项 */
export type AppLocale = Locale;

export const LOCALE_STORAGE_KEY = "app_locale";

/** 首次访问默认英文，与原型 LocaleProvider 一致 */
export const DEFAULT_LOCALE: AppLocale = "en";

const HTML_LANG: Record<AppLocale, string> = {
  en: "en",
  zh: "zh-CN",
  id: "id",
};

/**
 * 把本地缓存解析为合法语言；非法或空则回落英文。
 */
export function resolveAppLocale(raw: string | null): AppLocale {
  if (raw === "en" || raw === "zh" || raw === "id") return raw;
  return DEFAULT_LOCALE;
}

/**
 * 同步 html lang，供无障碍与浏览器翻译提示使用。
 */
export function applyDocumentLang(locale: AppLocale): void {
  // node 测试环境 / SSR 无 document：跳过即可，语言状态本身照常工作
  if (typeof document === "undefined") return;
  document.documentElement.lang = HTML_LANG[locale];
}

/**
 * 顶栏语言切换：持久化偏好，并驱动界面文案 / Element Plus 语言包。
 */
export const useLocaleStore = defineStore("locale", {
  state: () => {
    // 语言切换已隐藏，强制英文，忽略本地缓存
    // const locale = resolveAppLocale(typeof localStorage === "undefined" ? null : localStorage.getItem(LOCALE_STORAGE_KEY));
    // ⚠️ 显式标注 AppLocale：`const locale = DEFAULT_LOCALE` 会把 state 推断成字面量
    // `"en"`，导致全项目 `locale === "zh"` 这类比较被 TS 判成「无重叠」而报错。
    const locale: AppLocale = DEFAULT_LOCALE;
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
    applyDocumentLang(locale);
    // as AppLocale：阻断 TS 对 const 字面量的收窄，否则 state 被锁成 "en"，
    // 全项目 locale === "zh"/"id" 比较会报 TS2367「无重叠」
    return { locale: locale as AppLocale };
  },
  actions: {
    /**
     * 切换界面语言偏好并写入本地缓存。
     * 当前入口已隐藏，忽略入参并保持英文。
     */
    setLocale(locale: AppLocale) {
      // this.locale = locale;
      // localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      // applyDocumentLang(locale);
      void locale;
      this.locale = DEFAULT_LOCALE;
      localStorage.setItem(LOCALE_STORAGE_KEY, DEFAULT_LOCALE);
      applyDocumentLang(DEFAULT_LOCALE);
    },
  },
});
